/**
 * 成员名单 + **管理动作**（L 域 §5.1 / §5.6 · `AC-L5.13` / `AC-L5.19`）
 *
 * 依据：L 域 PRD **§5.1-2**（转让是获得 owner 的**唯一**途径）· **§5.6**（移除 = 一次单人拆分）·
 * `DR-018`（七项协作动作仅 owner）· 契约 `LedgerMemberView`（`isSelf` 由**服务端**判）。
 *
 * ## 🔴 三条判断
 *
 * | # | 判断 | 为什么 |
 * |:--:|---|---|
 * | 1 | **名单全员可见，管理按钮仅 owner** | `AC-L6.1`：member 与 owner 的**查询结果逐行相同** ⇒ 名单不能因角色而不同；变的只是"能否操作" |
 * | 2 | 🔴 **自己那一行不画"转让/移除"** | 服务端也拒（`transfer` 的目标不能是自己、移除自己要另走"退出"），但**画出来就是骗人** |
 * | 3 | 🔴 **不按角色隐藏数据，只隐藏入口** | 架构 §6.3 明文：`role` **仅用于隐藏协作动作入口**，不得据此隐藏数据 |
 *
 * ## ⚠️ 本组件**不发请求**
 *
 * 会员名单的唯一查询在 `SettingsPage`（`['ledgerMembers']`，顶栏头像堆叠共用同一个 queryKey）。
 * 本组件只把 `members` 渲染出来 + 把动作回调抛给页面 —— 页面管对话框与 mutation。
 * 这样"成员名单只有一份数据"这件事是**结构上成立**的，不靠自觉（铁律 24）。
 */

import { Button, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import type { LedgerMemberView, LedgerRole } from '@jiaying/shared';
import { canManageCollab, memberDisplayName, memberRoleText } from '../features/collab.js';

export interface MemberAdminListProps {
  members: readonly LedgerMemberView[];
  /** 调用者角色（决定画不画管理按钮） */
  role: LedgerRole | string;
  onTransfer: (m: LedgerMemberView) => void;
  onRemove: (m: LedgerMemberView) => void;
}

export function MemberAdminList({
  members,
  role,
  onTransfer,
  onRemove,
}: MemberAdminListProps): ReactElement {
  const canManage = canManageCollab(role);

  return (
    <List dense disablePadding data-testid="member-admin-list">
      {members.map((m) => {
        // 🔴 自己那一行不画动作（见文件头判断 2）
        const actionable = canManage && !m.isSelf;
        return (
          <ListItem
            key={m.userId}
            disableGutters
            data-testid={`member-row-${m.userId}`}
            secondaryAction={
              actionable ? (
                <Stack direction="row" spacing={0.5}>
                  <Button
                    size="small"
                    onClick={() => {
                      onTransfer(m);
                    }}
                    data-testid={`member-transfer-${m.userId}`}
                  >
                    转让
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      onRemove(m);
                    }}
                    data-testid={`member-remove-${m.userId}`}
                  >
                    移除
                  </Button>
                </Stack>
              ) : undefined
            }
          >
            <ListItemText
              primary={
                <Typography variant="body2" component="span" data-testid={`member-name-${m.userId}`}>
                  {memberDisplayName(m)}
                </Typography>
              }
              secondary={memberRoleText(m.role)}
            />
          </ListItem>
        );
      })}
    </List>
  );
}
